/**
 * @prop --vx-global-search-initial-width: The initial width of the global-search. Default is 282px.
 * @prop --vx-global-search-expanded-width: The expanded width of the global-search. Default is 800px.
 * @prop --vx-global-search-background: The color of the form background.
 * @prop --vx-global-search-background: The color of the form background.
 * @prop --vx-global-search-border-color: The border color of the form.
 * @prop --vx-global-search-border-radius: The border radius of the form.
 * @prop --vx-global-search-border-color-focus: The border color of the focused form.
 * @prop --vx-global-search-border-width-focus: The border width of the focused form.
 * @prop --vx-global-search-input-placeholder-color: The color of the input placeholder.
 * @prop --vx-global-search-suggestions-max-height: The max height of the suggestions container. Default is 600;
 * @prop --vx-global-search-expanded-with: The width of the whole form and suggestions. Default is 860;
 * @prop --vx-global-search-suggestions-border: The border of the suggestions container.
 * @prop --vx-global-search-suggestions-box-shadow: The box-shadow of the suggestions container.
 * @prop --animation-duration: The duration of any animations.
 * @prop --animation-delay: The delay of any animations.
 * @prop --scrollbar-thumb-color: The color of the scrollbar thumb. Default is --mdc-theme-primary or #333.
 */
@keyframes hide-scroll {
  from, to {
    overflow: hidden;
  }
}
[part=wrapper] {
  display: flex;
  background: var(--vx-global-search-background, #fff);
}
[part=wrapper] [part=suffix-icon] {
  display: none;
}
[part=wrapper] form {
  overflow: hidden;
  display: flex;
  transition: width ease-in-out var(--animation-duration, 0.3s);
  transition-delay: 0.1s;
  left: 0;
  width: 0;
  position: absolute;
  background-color: inherit;
  box-sizing: border-box;
  flex-direction: row;
  align-items: center;
  z-index: 2000;
}
[part=wrapper] form input {
  background-color: inherit;
  text-overflow: ellipsis;
  line-height: 24px;
  padding: 12px 0;
  width: 100%;
  outline: none;
  border: none;
  font-size: inherit;
  font-weight: inherit;
  font-family: inherit;
  font-stretch: inherit;
  color: currentcolor;
}
[part=wrapper] form input::placeholder {
  color: var(--vx-global-search-input-placeholder-color, var(--placeholder-color, #757575));
}
[part=wrapper] form input::-webkit-search-cancel-button {
  display: none;
}
[part=wrapper] form, [part=wrapper] input {
  border-radius: var(--mdc-shape-small, 4px);
}
[part=wrapper] [part=close-button] {
  transition: var(--animation-duration, 0.3s) ease-in-out;
  transition-property: width, margin-right;
  max-width: 114px;
  width: 0;
  overflow: hidden;
}
[part=wrapper] [part=close-button]::before {
  display: none;
  content: "";
  height: 36px;
  position: absolute;
  width: 4px;
  border-left: 1px solid var(--vx-global-search-border-color, #e5dfdf);
}
[part=wrapper] [part=suggestions] {
  box-shadow: var(--vx-global-search-suggestions-box-shadow, none);
  user-select: none;
  box-sizing: border-box;
  position: absolute;
  background: inherit;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 0;
  opacity: 0.8;
  overflow-y: auto;
  border-bottom-right-radius: var(--mdc-shape-small, 4px);
  border-bottom-left-radius: var(--mdc-shape-small, 4px);
  transition: var(--animation-duration, 0.3s) ease-in-out;
  transition-delay: 0.1s;
  transition-property: max-height, opacity;
}
[part=wrapper] [part=suggestions]::after {
  content: "";
  transition: opacity linear 0.5s;
  transition-delay: 0.5s;
  opacity: 0;
  position: absolute;
  background-color: inherit;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: -1;
}
[part=wrapper] [part=suggestions].is--loading::after {
  opacity: 1;
  z-index: 1;
}
[part=wrapper]:focus-within form {
  border-width: var(--vx-global-search-border-width-focus, 1px);
  border-color: var(--vx-global-search-border-color-focus, #000);
  width: 100vw;
}
[part=wrapper]:focus-within [part=suggestions].is--open {
  animation: hide-scroll calc(var(--animation-duration, 0.3s) + 0.1s) backwards;
  animation-delay: 0s;
  max-height: calc(100vh - var(--mobile-suggestions-top-value));
  opacity: 1;
}

@media screen and (min-width: 1181px) {
  [part=wrapper] {
    position: relative;
    width: unset;
    padding: 0;
    box-shadow: -70px 0 10px rgba(255, 255, 255, 0.7);
  }
  [part=wrapper] [part=suffix-icon] {
    display: block;
    padding: 0 8px;
  }
  [part=wrapper] [part=close-clear-button],
  [part=wrapper] [part=clear-button] {
    display: none;
  }
  [part=wrapper] [part=toggle-button] {
    width: 0;
    overflow: hidden;
  }
  [part=wrapper] form {
    display: flex;
    border: var(--vx-global-search-border-width-focus, 1px) solid var(--vx-global-search-border-color, var(--mdc-border-color, #cfc6c6));
    margin: 0;
    width: var(--vx-global-search-initial-width);
    position: relative;
  }
  [part=wrapper] form input {
    padding: 12px 16px;
  }
  [part=wrapper] [part=suggestions] {
    overflow: hidden;
  }
  [part=wrapper] [part=suggestions]::-webkit-scrollbar {
    width: 8px;
  }
  [part=wrapper] [part=suggestions]::-webkit-scrollbar-track {
    background-color: inherit;
  }
  [part=wrapper] [part=suggestions]::-webkit-scrollbar-thumb {
    background-color: var(--scrollbar-thumb-color, #454545);
  }
  [part=wrapper]:focus-within form {
    width: var(--vx-global-search-expanded-width, var(--vx-global-search-initial-width));
  }
  [part=wrapper]:focus-within form [part=suffix-icon] {
    display: none;
  }
  [part=wrapper]:focus-within form [part=clear-button] {
    display: block;
  }
  [part=wrapper]:focus-within [part=close-button] {
    width: 100%;
    margin-right: 4px;
  }
  [part=wrapper]:focus-within [part=close-button]::before {
    display: block;
  }
  [part=wrapper]:focus-within [part=suggestions].is--open {
    overflow-y: auto;
  }
}